import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "b24_auth_token";

export function getStoredToken() {
    try {
        return localStorage.getItem(TOKEN_KEY) || "";
    } catch {
        return "";
    }
}

export function setStoredToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
    } catch {}
}

const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// Attach Bearer token to every request — works even when cross-origin cookies are blocked.
api.interceptors.request.use((config) => {
    const t = getStoredToken();
    if (t) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${t}`;
        }
    }
    return config;
});

// 402 = trial expired / payment required → route to subscription page.
// 502/504/524 = gateway timeout (LLM-Task > 60s) → friendly German message instead of Cloudflare HTML.
api.interceptors.response.use(
    (resp) => resp,
    (err) => {
        const status = err?.response?.status;
        if (status === 402) {
            // Nur bei abgelaufener Testphase global umleiten — Feature-Sperren (Abo nötig)
            // zeigen ihre eigene Meldung/UpgradeGate auf der Seite.
            const detail = err?.response?.data?.detail;
            const isTrialExpired = typeof detail === "string" && detail.includes("Testphase ist abgelaufen");
            if (isTrialExpired) {
                try {
                    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/dashboard/abo")) {
                        window.location.href = "/dashboard/abo?trial=expired";
                    }
                } catch {}
            }
        }
        if ([502, 504, 524].includes(status)) {
            err.response.data = {
                detail: "Der Server braucht gerade etwas länger. Bitte warte einen Moment und versuche es erneut.",
            };
        }
        return Promise.reject(err);
    },
);

export default api;

// Pollt einen KI-Hintergrund-Job bis done/error (verhindert Proxy-Timeouts bei langen KI-Läufen)
export async function pollAiJob(jobId, { interval = 3000, timeout = 300000 } = {}) {
    const start = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (Date.now() - start > timeout) throw new Error("Zeitüberschreitung — bitte erneut versuchen");
        await new Promise((r) => setTimeout(r, interval));
        const { data } = await api.get(`/ai-jobs/${jobId}`);
        if (data.status === "done") return data.result;
        if (data.status === "error") throw new Error(data.error || "KI-Verarbeitung fehlgeschlagen");
    }
}

export function formatApiError(detail) {
    if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export async function startPackageCheckout(packageId, quantity = 1) {
    const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
        quantity,
    });
    if (data.url) window.location.href = data.url;
}
